import { useState } from 'react';
import { Modal, Group, Text, Button, Paper, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { Net10PendingPayment } from '../../../../services/api/billcom';
import { formatCents, formatDateOnly } from '../../../../utils/formatters';
import { getNet10SessionDetail } from '../../../../config/compensationConfig';

interface SubmitSingleNet10ModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  payment: Net10PendingPayment | null;
  onSuccess: () => void;
}

export function SubmitSingleNet10Modal({
  opened,
  onClose,
  cohortId,
  payment,
  onSuccess,
}: SubmitSingleNet10ModalProps) {
  const [submitting, setSubmitting] = useState(false);

  if (!payment) return null;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await cohortBillcomApi.submitNet10Payment(cohortId, {
        event_reference_id: payment.event_reference_id,
        event_reference_type: payment.event_reference_type,
        coach_user_id: payment.coach_user_id,
      });
      notifications.show({
        title: 'Payment Submitted',
        message: `Payment for ${payment.coach_name} submitted to Bill.com`,
        color: 'green',
      });
      onSuccess();
      onClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to submit payment';
      notifications.show({
        title: 'Payment not submitted',
        message: msg,
        color: 'red',
        autoClose: false,
      });
      // MED-1383: the API marks the row failed before it answers, so reload the tables
      // to show the row and its reason in Sent to Bill.com.
      onSuccess();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Submit Payment to Bill.com
        </Text>
      }
      size="md"
      centered
    >
      <Paper
        p="md"
        radius="sm"
        style={{ background: 'var(--medley-bg-main, #F5F3EF)' }}
      >
        <Stack gap="sm">
          <Group>
            <Text size="sm" c="dimmed" w={90}>
              Coach
            </Text>
            <Text size="sm" fw={600}>
              {payment.coach_name}
            </Text>
          </Group>
          <Group>
            <Text size="sm" c="dimmed" w={90}>
              Event
            </Text>
            <Text size="sm">{payment.event_name}</Text>
          </Group>
          {/* MED-1383: which session this pays, when one coach has several 1:1 rows. */}
          {getNet10SessionDetail(payment) && (
            <Group>
              <Text size="sm" c="dimmed" w={90}>
                Session
              </Text>
              <Text size="sm">{getNet10SessionDetail(payment)}</Text>
            </Group>
          )}
          <Group>
            <Text size="sm" c="dimmed" w={90}>
              Amount
            </Text>
            <Text size="sm" fw={600}>
              {formatCents(payment.amount_cents)}
            </Text>
          </Group>
          <Group>
            <Text size="sm" c="dimmed" w={90}>
              Due Date
            </Text>
            <Text size="sm" c={payment.is_overdue ? 'dangerRed' : undefined} fw={payment.is_overdue ? 600 : undefined}>
              {formatDateOnly(payment.due_date)}
            </Text>
          </Group>
        </Stack>
      </Paper>

      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="medleyGreen.9"
          onClick={handleSubmit}
          loading={submitting}
        >
          Submit Payment
        </Button>
      </Group>
    </Modal>
  );
}
